/**
 * components/BetaApplication/BetaApplicationWizard.tsx
 * Beta Application Wizard - Professional beta application experience
 *
 * Provides a comprehensive, step-by-step beta application flow with:
 * - 3-step wizard: Account Info, Trading Profile, Beta Commitment
 * - WizardLayout integration with progress tracking
 * - Type-safe data management with localStorage persistence
 * - Professional validation and error handling
 * - Mobile-responsive design following application patterns
 */

// 1. External dependencies
import React, { useRef, useEffect, useMemo, useState } from 'react';

// 2. Internal absolute imports
import { ScrollShadowRef } from '@enfomentis/ui';
import { WizardLayout } from '@/components/shared/layouts';
import { useWizardNavigation, type WizardStep } from '@/components/shared/layouts/WizardLayout/useWizardNavigation';
import { Brand } from '@enfomentis/shared/config';

// 3. Relative imports
import type { BetaApplicationDraft } from './types/BetaApplicationDraft';
import { canCompleteStep } from './utils/betaApplicationValidation';
import { WelcomeStep } from './steps/WelcomeStep';
import { AccountInformationStep } from './steps/AccountInformationStep';
import { TradingSetupStep } from './steps/TradingSetupStep';
import { StyleExperienceStep } from './steps/StyleExperienceStep';
import { BetaCommitmentStep } from './steps/BetaCommitmentStep';
import { ApplicationReviewStep } from './steps/ApplicationReviewStep';
import { CompletionStep } from './steps/CompletionStep';
import { useBetaApplicationData } from './hooks/useBetaApplicationData';

// 4. Supabase integration imports
import { betaApplicationService } from '@/services/BetaApplicationService';
import {
  convertDraftToApplication,
  validateDraftApplication,
  isDraftComplete
} from '@/utils/betaApplicationConverters';

/**
 * Beta application wizard step definitions
 */
const BETA_APPLICATION_STEPS: WizardStep<BetaApplicationDraft>[] = [
  {
    id: 'welcome',
    name: 'Welcome',
    title: `Welcome to ${Brand.displayName} Beta Program`,
    description: 'Learn about the exclusive beta testing opportunity',
    excludeFromProgress: true // Welcome screen doesn't count toward application progress
  },
  {
    id: 'account',
    name: 'Account Information',
    title: 'Step 1',
    description: 'Basic account and contact details',
    validation: (data) => data ? canCompleteStep('account', data) : false
  },
  {
    id: 'trading',
    name: 'Trading Setup',
    title: 'Step 2',
    description: 'Trading setup and account details',
    validation: (data) => data ? canCompleteStep('trading', data) : false
  },
  {
    id: 'style-experience',
    name: 'Style & Experience',
    title: 'Step 3',
    description: 'Trading style and experience details',
    validation: (data) => data ? canCompleteStep('style-experience', data) : false
  },
  {
    id: 'commitment',
    name: 'Beta Commitment',
    title: 'Step 4',
    description: 'Your commitment to beta testing',
    validation: (data) => data ? canCompleteStep('commitment', data) : false
  },
  {
    id: 'review',
    name: 'Review Application',
    title: 'Step 5',
    description: 'Review your complete application',
    validation: () => true // Review step is always valid (read-only)
  },
  {
    id: 'completion',
    name: 'Application Complete',
    title: 'Completion',
    description: 'Application submitted successfully',
    excludeFromProgress: true, // Don't show in progress since this is post-submission
    validation: () => true // Completion step is always valid
  }
];

/**
 * Main beta application wizard component
 */
export const BetaApplicationWizard: React.FC = () => {
  const { data: draft, resetData } = useBetaApplicationData();
  const scrollShadowRef = useRef<ScrollShadowRef>(null);
  const {
    currentStepIndex,
    currentStep,
    activeSteps,
    totalSteps,
    progress,
    totalProgressSteps,
    canNavigateNext,
    canNavigateBack,
    goNext,
    goBack
  } = useWizardNavigation({
    steps: BETA_APPLICATION_STEPS,
    data: draft as BetaApplicationDraft
  });


  // Calculate current progress step
  const currentProgressStep = useMemo((): number => {
    let progressStep = 0;
    for (let i = 0; i <= currentStepIndex && i < activeSteps.length; i++) {
      const step = activeSteps[i];
      if (step && !step.excludeFromProgress) {
        progressStep++;
      }
    }
    return progressStep;
  }, [activeSteps, currentStepIndex]);

  // Scroll to top when step changes
  useEffect(() => {
    if (scrollShadowRef.current) {
      scrollShadowRef.current.scrollToTop('instant');
    }
  }, [currentStepIndex]);

  // Initial scroll to top on mount
  useEffect(() => {
    if (scrollShadowRef.current) {
      scrollShadowRef.current.scrollToTop('instant');
    }
  }, []);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedApplicationId, setSubmittedApplicationId] = useState<string | null>(null);

  /**
   * Handle form submission (final step)
   */
  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault();

    if (!draft) {
      setSubmitError('Application data not found. Please try again.');
      return;
    }

    // Validate application before submission
    const validationErrors = validateDraftApplication(draft);
    if (validationErrors.length > 0) {
      setSubmitError(`Please fix the following issues:\n${validationErrors.join('\n')}`);
      return;
    }

    // Check if application is complete
    if (!isDraftComplete(draft)) {
      setSubmitError('Please complete all steps before submitting.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Check if email has already applied
      const hasApplied = await betaApplicationService.hasApplied(draft.account.email);
      if (hasApplied) {
        setSubmitError(`This email address has already submitted a beta application. If you think this is a mistake, please contact ${Brand.supportEmail}.`);
        return;
      }

      // Convert draft to application format
      const applicationData = convertDraftToApplication(draft);

      // Submit to Supabase and get the created application
      const submittedApplication = await betaApplicationService.add(applicationData);

      // Store the real application ID
      setSubmittedApplicationId(submittedApplication.id);

      // Mark as successfully submitted and navigate to completion step
      setSubmitSuccess(true);
      goNext(); // Navigate to completion step

      // Clear draft from localStorage after successful submission
      resetData();

      // Note: Confirmation email will be sent automatically by the scheduled email queue processor
      // The application is created with email_status='pending', and the processor will:
      // 1. Query for pending applications
      // 2. Send confirmation email via Resend
      // 3. Update email_status to 'sent' or 'failed'
      // 4. Record in communications table

      // TODO: [CONTRACTOR] Send notification to admin about new application

    } catch (error) {
      console.error('Application submission failed:', error);
      setSubmitError(
        error instanceof Error
          ? `Submission failed: ${error.message}`
          : 'An unexpected error occurred. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render the current step component
   */
  const renderCurrentStep = (): React.JSX.Element => {
    switch (currentStep.id) {
      case 'welcome':
        return <WelcomeStep />;
      case 'account':
        return <AccountInformationStep />;
      case 'trading':
        return <TradingSetupStep />;
      case 'style-experience':
        return <StyleExperienceStep />;
      case 'commitment':
        return <BetaCommitmentStep />;
      case 'review':
        return <ApplicationReviewStep />;
      case 'completion':
        return (
          <CompletionStep
            applicationId={submittedApplicationId || 'Unknown'}
            applicantName={draft?.account?.fullName || 'there'}
            showRedirect={false}
          />
        );
      default:
        return (
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-text-primary mb-2">
              {currentStep.title}
            </h2>
            <p className="text-text-secondary">
              Step {currentStepIndex + 1} of {totalSteps} - Coming Soon
            </p>
          </div>
        );
    }
  };

  if (!draft) {
    return (
      <WizardLayout
        currentStep="1"
        totalSteps={totalSteps}
        progress={0}
        stepName="Loading..."
        showHeader={false}
      >
        <div className="min-h-[400px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-default-500">Loading beta application...</p>
          </div>
        </div>
      </WizardLayout>
    );
  }

  // Prepare standardized navigation configuration
  const navigation = currentStep.id === 'welcome' ? {
    mode: 'welcome' as const,
    welcomeButton: {
      label: 'Start Application',
      onClick: goNext,
      color: 'primary' as const,
      size: 'lg' as const,
      className: 'px-8'
    }
  } : currentStep.id === 'completion' ? {
    mode: 'none' as const
  } : currentStep.id === 'review' ? {
    mode: 'navigation' as const,
    backButton: {
      label: 'Back',
      onClick: goBack,
      disabled: !canNavigateBack
    },
    nextButton: {
      label: submitSuccess ? "Application Submitted!" : isSubmitting ? "Submitting..." : "Submit Application",
      onClick: () => { handleSubmit().catch(console.error); },
      disabled: isSubmitting || submitSuccess,
      loading: isSubmitting,
      color: submitSuccess ? "success" as const : "primary" as const
    }
  } : {
    mode: 'navigation' as const,
    backButton: {
      label: 'Back',
      onClick: goBack,
      disabled: !canNavigateBack
    },
    nextButton: {
      label: 'Next',
      onClick: goNext,
      disabled: !canNavigateNext,
      color: 'primary' as const
    }
  };

  return (
    <WizardLayout
      currentStep={`${currentStepIndex + 1}`}
      totalSteps={totalSteps}
      progress={progress}
      stepName={currentStep.name}
      showHeader={currentStep.id !== 'welcome'}
      navigation={navigation}
      scrollRef={scrollShadowRef}
      currentProgressStep={currentProgressStep}
      totalProgressSteps={totalProgressSteps}
      excludeCurrentFromProgress={!!currentStep.excludeFromProgress}
      title={`${Brand.shortName} Beta Application`}
    >
      {/* Error Display */}
      {submitError && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg">
          <h4 className="font-semibold text-danger-800 mb-2">Submission Error</h4>
          <p className="text-sm text-danger-700 whitespace-pre-line">{submitError}</p>
        </div>
      )}


      {renderCurrentStep()}
    </WizardLayout>
  );
};